import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";

import { loanDrilldownSidebarTypes } from "../constants/enums/loanDrilldownScreenSidebarOptions";
import {
  sidebarSetStateValue,
  sidebarTypeSet,
} from "../redux/actions/sidebarActions";
import { loanFetchedDataUwRequirementsSet } from "../redux/reducers/loanFetchedDataReducer";
import { useChecklistRouteHook } from "./useChecklistRouteHook";

const escapeHtml = (s) =>
  String(s || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

/** A sensible starter line for a quick condition off a document. */
export const defaultConditionText = (doc) =>
  `Get updated ${doc?.docType || doc?.docGroup || "document"}`;

// Build the create payload that pre-attaches the viewed document and prefills
// what we know about it. Creating a condition FROM a document defaults to a
// `Document` requirement (you're asking for this doc / an updated version), with
// the doc's group + type pre-selected when it has them. The user can switch the
// type to Condition/Stip in the full form if they meant something else.
const buildSeed = (doc, { text } = {}) => {
  const docId = doc?._id || doc?.id;
  const docType = doc?.docType || null;
  const docGroup = doc?.docGroup || null;
  const seed = {
    requirementType: "Document",
    docStipStatus: "Open Item",
    uploadedDocumentIds: docId ? [docId] : [],
    associations: docId ? { loanDocuments: [docId] } : {},
  };
  if (docGroup) seed.docGroup = docGroup;
  if (docType) seed.docType = docType;
  if (text != null && text !== "") seed.htmlString = `<p>${escapeHtml(text)}</p>`;
  return seed;
};

/**
 * Create a loan condition from the document the user is viewing, with that
 * document auto-attached (`uploadedDocumentIds` + `associations.loanDocuments`)
 * and what we know about it (docGroup / docType) prefilled.
 *
 * Two paths:
 *  - `quickCreate(doc, { text })` — creates immediately from a one-line
 *    description (e.g. "Get updated bank statement"). No sidebar; stays on the
 *    file. Returns the create promise.
 *  - `openForDocument(doc, { text })` — opens the full Add Condition sidebar
 *    pre-seeded, for when more detail (due date, assignee, etc.) is wanted.
 *
 * `canCreate` is false outside a loan context (the action hides there).
 */
export const useCreateConditionFromDocument = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const loanId = useSelector((state) => state.loanDrilldown?._id);
  const { loading, createLoanUnderwritingRequirement } = useChecklistRouteHook();

  const openForDocument = useCallback(
    (doc, opts = {}) => {
      const docId = doc?._id || doc?.id;
      if (!docId) return;
      // No `_id` on the seed → create mode in the condition sidebar.
      dispatch(sidebarSetStateValue(buildSeed(doc, opts)));
      dispatch(
        sidebarTypeSet(
          loanDrilldownSidebarTypes.ADD_EDIT_LOAN_CONDITION_REDESIGN,
        ),
      );
    },
    [dispatch],
  );

  const quickCreate = useCallback(
    (doc, { text, onSuccess, onError } = {}) => {
      const docId = doc?._id || doc?.id;
      if (!loanId || !docId) return undefined;
      const data = buildSeed(doc, {
        text: text ?? defaultConditionText(doc),
      });
      return createLoanUnderwritingRequirement({
        dealId: loanId,
        data,
        onSuccessFn: (response) => {
          if (Array.isArray(response)) {
            dispatch(loanFetchedDataUwRequirementsSet(response));
          }
          enqueueSnackbar("Condition created", { variant: "success" });
          onSuccess?.(response);
        },
        onFailFn: (err) => {
          enqueueSnackbar("Couldn't create condition", { variant: "error" });
          onError?.(err);
        },
      });
    },
    [loanId, createLoanUnderwritingRequirement, dispatch, enqueueSnackbar],
  );

  return {
    openForDocument,
    quickCreate,
    creating: loading,
    canCreate: !!loanId,
  };
};

export default useCreateConditionFromDocument;
